// @ts-nocheck

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image, FileText, Upload, Plus, Trash2, Download } from 'lucide-react';
import { useAffiliatePromoAssets } from '@/hooks/useAffiliatePromoAssets';
import UploadPromoAssetModal from './UploadPromoAssetModal';

const AffiliatePromoMaterialsTab = () => {
  const { assets, affiliates, loading, uploadAsset, deleteAsset } = useAffiliatePromoAssets();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const handleDeleteAsset = async (assetId: string, filePath: string) => {
    if (confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      await deleteAsset(assetId, filePath);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAssignmentText = (asset: any) => {
    if (asset.assigned_to_all) {
      return 'All Affiliates';
    }
    const count = asset.assigned_affiliate_ids?.length || 0;
    return count === 1 ? '1 Affiliate' : `${count} Affiliates`;
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload Marketing Materials
          </CardTitle>
          <CardDescription>
            Upload banners, images, and other promotional materials for affiliates to use
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <Button onClick={() => setUploadModalOpen(true)} size="lg">
              <Plus className="h-4 w-4 mr-2" />
              Upload New Asset
            </Button>
            <p className="text-sm text-gray-600 mt-2">
              Supported formats: JPG, PNG, WebP (max 10MB)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Current Materials */}
      <Card>
        <CardHeader>
          <CardTitle>Current Marketing Materials</CardTitle>
          <CardDescription>
            Manage uploaded promotional assets available to affiliates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading assets...</p>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Materials Available</h3>
              <p className="text-gray-600 mb-6">
                Upload promotional materials for affiliates to access and use in their marketing campaigns.
              </p>
              <Button onClick={() => setUploadModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload First Asset
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <div key={asset.id} className="border rounded-lg overflow-hidden">
                  <div className="aspect-video bg-gray-100 relative">
                    <img
                      src={asset.file_url}
                      alt={asset.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden w-full h-full flex items-center justify-center bg-gray-100">
                      <Image className="h-12 w-12 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h4 className="font-medium text-sm mb-2 truncate" title={asset.name}>
                      {asset.name}
                    </h4>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>Size: {formatFileSize(asset.file_size)}</p>
                      <p>Assigned: {getAssignmentText(asset)}</p>
                      <p>Uploaded: {new Date(asset.created_at).toLocaleDateString()}</p>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => window.open(asset.file_url, '_blank')}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAsset(asset.id, asset.file_path)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Material Categories Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Total Assets</CardTitle>
            <CardDescription>All promotional materials</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">{assets.length} assets uploaded</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Global Assets</CardTitle>
            <CardDescription>Available to all affiliates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {assets.filter(asset => asset.assigned_to_all).length} global assets
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Targeted Assets</CardTitle>
            <CardDescription>Assigned to specific affiliates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {assets.filter(asset => !asset.assigned_to_all).length} targeted assets
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <UploadPromoAssetModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={uploadAsset}
        affiliates={affiliates}
      />
    </div>
  );
};

export default AffiliatePromoMaterialsTab;
